import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { expenseDetails } from "../../../core/data/json/expenseDetails";
import Table from "../../../core/common/dataTable/index";
import ReactApexChart from "react-apexcharts";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { formatDate } from "../../../core/utils/FormatData";
import { AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllExpenses } from "../../../core/data/redux/payroll/Accounting/expenses/expenseActions";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import {
  fetchAllExpensesReport,
  fetchAllExpensesReportCount,
  fetchAllRequestCount,
} from "../../../core/data/redux/reports/expenses_report/expenses.Action";
import {
  exportToCSV,
  exportToExcel,
} from "../../../core/common/GlobalFunctions/exportTableData";
import { fetchAllEsicCount, fetchAllEsicDetails, fetchAllEsicMonthlyCount } from "../../../core/data/redux/reports/esic_report/esic.Action";
import { Container, Row, Col, Form } from "react-bootstrap";


const EsicContributionCustom = ({ data = [] }: { data?: any }) => {
  const normalize = (arr: any) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((emp) => ({
      EmployeeName: emp.EmployeeName ?? emp.Name ?? "-",
      esic_number: emp.esic_number ?? emp.esicNumber ?? emp.esicNumber ?? "-",
      Year: emp.Year ?? emp.year ?? null,
      Month: emp.Month ?? emp.month ?? null,
      TotalGross: emp.Gross ?? emp.TotalGross ?? "0.00",
      TotalEsicWages: emp.EsicWages ?? emp.TotalEsicWages ?? "0.00",
      TotalEmployeeContribution:
        emp.EmployeeContribution ?? emp.TotalEmployeeContribution ?? "0.00",
      TotalEmployerContribution:
        emp.EmployerContribution ?? emp.TotalEmployerContribution ?? "0.00",
      TotalContributionAmount:
        emp.TotalContributionAmount ??
        emp.Total ??
        emp.TotalContribution ??
        (
          (+emp.Gross || +emp.TotalGross || 0) +
          (+emp.EsicWages || +emp.TotalEsicWages || 0) +
          (+emp.EmployeeContribution || +emp.TotalEmployeeContribution || 0) +
          (+emp.EmployerContribution || +emp.TotalEmployerContribution || 0)
        ).toFixed(2),
    }));
  };

  const normalized = useMemo(() => normalize(data || []), [data]);

  const [searchEmployee, setSearchEmployee] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");

  const months = [
    { value: "", label: "All Months" },
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const filteredData = useMemo(() => {
    return normalized.filter((item) => {
      const matchEmployee = item.EmployeeName
        ?.toString()
        .toLowerCase()
        .includes((searchEmployee || "").toLowerCase());

      const matchYear = year ? item.Year?.toString() === year : true;

      // Normalize month in item to two-digit string if present
      const itemMonth =
        item.Month?.toString().length === 1
          ? item.Month?.toString().padStart(2, "0")
          : item.Month?.toString();

      const matchMonth = month ? itemMonth === month : true;

      return matchEmployee && matchYear && matchMonth;
    });
  }, [searchEmployee, year, month, normalized]);

  return (
    <>
      <style>{`
        .custom-table {
          width: 100%;
          border: 1px solid #ccc;
          border-radius: 6px;
          overflow-y: auto;
          max-height: 520px;
        }
        
        .table-row {
          display: grid;
          grid-template-columns: 2fr repeat(6, 1fr);
          padding: 10px 12px;
          border-bottom: 1px solid #eee;
          align-items: center;
        }

        .table-header {
          background: #e3e7fd;
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 2;
        }

        .cell {
          padding: 6px 8px;
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .table-row:hover:not(.table-header) {
          background: #f3f6ff;
        }

        .no-data {
          padding: 15px;
          text-align: center;
          color: #b30000;
          font-weight: 600;
        }
      `}</style>

      <Container className="mt-3 p-0">
        <Row className="g-3 align-items-center mb-2">
          <Col md={4}>
            <Form.Control
              placeholder="Search Employee..."
              value={searchEmployee}
              onChange={(e) => setSearchEmployee(e.target.value)}
            />
          </Col>

          <Col md={2}>
            <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
              <option value="">All Years</option>
              <option value="2023">2023</option>
              <option value="2024">2024</option>
              <option value="2025">2025</option>
            </Form.Select>
          </Col>

          <Col md={2}>
            <Form.Select value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <div className="custom-table">
          <div className="table-row table-header">
            <div className="cell">Employee Name</div>
            <div className="cell">ESIC No</div>
            <div className="cell">Gross</div>
            <div className="cell">ESIC Wages</div>
            <div className="cell">Emp Contribution</div>
            <div className="cell">Employer Contribution</div>
            <div className="cell">Total</div>
          </div>

          {filteredData.length === 0 ? (
            <div className="no-data">No Records Found</div>
          ) : (
            filteredData.map((item: any, idx: number) => (
              <div className="table-row" key={idx}>
                <div className="cell">{item.EmployeeName}</div>
                <div className="cell">{item.esic_number ?? "-"}</div>
                <div className="cell">{item.TotalGross ?? "0.00"}</div>
                <div className="cell">{item.TotalEsicWages ?? "0.00"}</div>
                <div className="cell">{item.TotalEmployeeContribution ?? "0.00"}</div>
                <div className="cell">{item.TotalEmployerContribution ?? "0.00"}</div>
                <div className="cell">{item.TotalContributionAmount ?? "0.00"}</div>
              </div>
            ))
          )}
        </div>
      </Container>
    </>
  );
};



const ESI_Reports = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { records } = useSelector((state: any) => state.accounting.expenses);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [selectedYear, setSelectedYear] = useState("2025");
  const [chartdata, setchartdata] = useState<any[]>([]);
  const [count, setcount] = useState<any>("");
  const [request, setrequest] = useState<any>([]);
  console.log(request, 'request');
  useEffect(() => {
    dispatch(fetchAllEsicMonthlyCount(selectedYear)).then((res) => {
      setchartdata(res.payload.data);
    });
    dispatch(fetchAllEsicCount()).then((res) => {
      setcount(res.payload.data);
    });
    dispatch(fetchAllEsicDetails(selectedYear))
      .then((res) => {
        setrequest(res.payload.data.Employees)
      })
  }, []);

  const data = records ? records : [];

  function filterExpenseData(data: any[]) {
    return data.map(expense => ({
      expenseName: expense.expense_name || "N/A",
      paymentMethod: expense.payment_method || "N/A",
      requestStatus: expense.requestStatus || "N/A",
      paymentStatus: expense.paymentStatus || "N/A",
      expenseDate: new Date(expense.expense_date).toLocaleDateString(),
      amount: expense.amount || "0.00"
    }));
  }

  const data2 = filterExpenseData(data)


  const filteredData = useMemo(() => {
    return filterData(data || [], {}, "CreatedAt", "CreatedAt", selectedSort);
  }, [dispatch, records, selectedSort, selectedYear]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedStatus, selectedSort, records, selectedYear]);



  useEffect(() => {
    const expensePerMonth = new Array(12).fill(0);

    if (chartdata && chartdata.length > 0) {
      chartdata.forEach(({ month, total_contribution_amount }: any) => {
        // Extract Month number from "2025-01"
        const monthNumber = parseInt(month.split("-")[1]); // "01" → 1
        expensePerMonth[monthNumber - 1] = parseFloat(total_contribution_amount);
      });
    }

    setexpensechart((prev: any) => ({
      ...prev,
      series: [
        {
          ...prev.series[0],
          data: expensePerMonth,
        },
      ],
    }));
  }, [chartdata, dispatch]);


  useEffect(() => {
    dispatch(fetchAllEsicMonthlyCount(selectedYear)).then((res) => {
      setchartdata(res.payload.data);
    });
  }, [dispatch, selectedYear]);


  const handleYearSelect = (year: any) => {
    setSelectedYear(year);
  };



  //New Chart
  const [expensechart, setexpensechart] = useState<any>({
    series: [
      {
        name: "ESIC Contribution",
        data: new Array(12).fill(0),
      },
    ],
    chart: {
      height: 190,
      type: "area",
      zoom: {
        enabled: false,
      },
    },
    colors: ["#FF9F43"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
    },
    title: {
      text: "",
      align: "left",
    },
    // grid: {
    //   row: {
    //     colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
    //     opacity: 0.5
    //   },
    // },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
    },
    yaxis: {
      min: 0,
      max: 100000,
      tickAmount: 5,
      labels: {
        offsetX: -15,
        formatter: (val: number) => val >= 1000 ? (val / 1000).toFixed(1) + "K" : val
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
    },
  });



  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">ESIC Report</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="index.html">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Esic Report
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <button
                        onClick={() => exportToCSV(data2, "ESIC_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-pdf me-1" />
                        Export as CSV
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => exportToExcel(data2, "ESIC_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel{" "}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row">
            {/* Total Exponses */}
            <div className="col-xl-6 d-flex">
              <div className="row flex-fill">
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/total-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            Total Configuration
                          </span>
                          <h5>{count ? count?.[0].total_esic_configurations : 0}</h5>
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-primary border border-primary"
                        >
                          <span className="text-primary">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-success fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    +20.01%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/approved-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            Total Branch
                          </span>
                          <h5>{count ? count?.[0].total_esic_branches : 0}</h5>
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-success border border-success"
                        >
                          <span className="text-success">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-success fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    +17.01%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/pending-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            Employee Mapped
                          </span>
                          <h5>{count ? count?.[0].total_esic_employees : 0}</h5>
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-skyblue border border-skyblue"
                        >
                          <span className="text-skyblue">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-success fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    +10.13%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/reject-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            Total Benefit_Type
                          </span>
                          <h5>{count ? count?.[0].total_esic_benefit_types : 0}</h5>
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-danger border border-danger"
                        >
                          <span className="text-danger">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-danger fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    -10.17%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Total Exponses */}
            {/* Total Exponses */}
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header border-0 pb-0">
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="d-flex align-items-center ">
                      <span className="me-2">
                        <i className="ti ti-chart-area-line text-danger" />
                      </span>
                      <h5>Contribution </h5>
                    </div>
                    <div className="dropdown">
                      <Link
                        to="#"
                        className="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                        data-bs-toggle="dropdown"
                      >
                        {selectedYear}
                      </Link>
                      <ul className="dropdown-menu  dropdown-menu-end p-2">
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2023")}
                          >
                            2023
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2024")}
                          >
                            2024
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2025")}
                          >
                            2025
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2026")}
                          >
                            2026
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="card-body py-0">
                  <ReactApexChart
                    id="expense-analysis"
                    options={expensechart}
                    series={expensechart.series}
                    type="area"
                    height={193}
                  />
                </div>
              </div>
            </div>
            {/* /Total Exponses */}
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Monthly Employee Contributions List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
              </div>
            </div>
            <div className="card-body p-0">
              <EsicContributionCustom data={request} />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default ESI_Reports;
